/**
 * Labstep
 *
 * @module state/reducers/ui-persistent
 * @desc Reducers for ui (persistent - will be rehydrated on app boot)
 */

import { ERRORS } from 'labstep-web/constants/error';
import type { ShareLink } from 'labstep-web/models/share-link.model';
import {
  State,
  type TypeSetUiCountPerPageAction,
  type TypeSetUiExperimentWorkflowViewAction,
  type TypeSetUiSearchParamsAction,
  type TypeToggleUiFilterAction,
} from 'labstep-web/state/actions/ui-persistent.types';
import isEqual from 'lodash/isEqual';
import { combineReducers } from 'redux';

export interface StateUIPersistent {
  filters: StateUIPersistentFilters;
  active_share_link: ShareLink | null;
  experiment_workflow_view: ExperimentWorkflowView;
  count_per_page: number;
  search_params: SearchParams;
  entity_view_params: Record<string, Record<string, unknown>>;
}

export enum StateUIPersistentState {
  collapsed = 'collapsed',
  uncollapsed = 'uncollapsed',
}

export interface FilterState {
  state: State;
}

export interface DeviceFilters {
  assignee: FilterState;
  created_by: FilterState;
  tag: FilterState;
}

export interface EntityStateFilters {
  unstarted: FilterState;
  started: FilterState;
  completed: FilterState;
}

export interface EntityViewFilters {
  created_by: FilterState;
  tag: FilterState;
}

export interface ExperimentWorkflowFilters {
  status: FilterState;
  status_type: FilterState;
  signatures: FilterState;
  assignee: FilterState;
  created_by: FilterState;
  tag: FilterState;
}

export interface ProtocolCollectionFilters {
  assignee: FilterState;
  created_by: FilterState;
  tag: FilterState;
}

export interface ResourceFilters {
  assignee: FilterState;
  resource_template: FilterState;
  created_by: FilterState;
  tag: FilterState;
}

export interface ResourceItemFilters {
  assignee: FilterState;
  status: FilterState;
  created_by: FilterState;
  tag: FilterState;
}

export interface OrderRequestFilters {
  assignee: FilterState;
  status: FilterState;
  created_by: FilterState;
  tag: FilterState;
}

export interface PurchaseOrderFilters {
  assignee: FilterState;
  status: FilterState;
  created_by: FilterState;
  tag: FilterState;
}

export interface FileFilters {
  extension: FilterState;
  created_by: FilterState;
  tag: FilterState;
}

export interface TagFilters {
  created_by: FilterState;
  tag: FilterState;
  tag_type: FilterState;
}

export interface StateUIPersistentFilters {
  device: DeviceFilters;
  entity_state: EntityStateFilters;
  entity_view: EntityViewFilters;
  experiment_workflow: ExperimentWorkflowFilters;
  protocol_collection: ProtocolCollectionFilters;
  resource: ResourceFilters;
  resource_item: ResourceItemFilters;
  order_request: OrderRequestFilters;
  purchase_order: PurchaseOrderFilters;
  file: FileFilters;
  tag: TagFilters;
}

export type ExperimentWorkflowView = 'table' | string;

export interface SearchParams {
  [pathname: string]: Record<string, any>;
}

export const initialStateFilters: StateUIPersistentFilters = {
  device: {
    assignee: {
      state: State.collapsed,
    },
    created_by: {
      state: State.collapsed,
    },
    tag: {
      state: State.collapsed,
    },
  },
  entity_state: {
    unstarted: {
      state: State.collapsed,
    },
    started: {
      state: State.collapsed,
    },
    completed: {
      state: State.collapsed,
    },
  },
  entity_view: {
    created_by: {
      state: State.collapsed,
    },
    tag: {
      state: State.collapsed,
    },
  },
  experiment_workflow: {
    status: {
      state: State.collapsed,
    },
    status_type: {
      state: State.collapsed,
    },
    signatures: {
      state: State.collapsed,
    },
    assignee: {
      state: State.collapsed,
    },
    created_by: {
      state: State.collapsed,
    },
    tag: {
      state: State.collapsed,
    },
  },
  protocol_collection: {
    assignee: {
      state: State.collapsed,
    },
    created_by: {
      state: State.collapsed,
    },
    tag: {
      state: State.collapsed,
    },
  },
  resource: {
    assignee: {
      state: State.collapsed,
    },
    resource_template: {
      state: State.uncollapsed,
    },
    created_by: {
      state: State.collapsed,
    },
    tag: {
      state: State.collapsed,
    },
  },
  resource_item: {
    assignee: {
      state: State.collapsed,
    },
    status: {
      state: State.uncollapsed,
    },
    created_by: {
      state: State.collapsed,
    },
    tag: {
      state: State.collapsed,
    },
  },
  order_request: {
    assignee: {
      state: State.collapsed,
    },
    status: {
      state: State.uncollapsed,
    },
    created_by: {
      state: State.collapsed,
    },
    tag: {
      state: State.collapsed,
    },
  },
  purchase_order: {
    assignee: {
      state: State.collapsed,
    },
    status: {
      state: State.uncollapsed,
    },
    created_by: {
      state: State.collapsed,
    },
    tag: {
      state: State.collapsed,
    },
  },
  file: {
    extension: {
      state: State.uncollapsed,
    },
    created_by: {
      state: State.collapsed,
    },
    tag: {
      state: State.collapsed,
    },
  },
  tag: {
    created_by: {
      state: State.collapsed,
    },
    tag: {
      state: State.collapsed,
    },
    tag_type: {
      state: State.uncollapsed,
    },
  },
};

export const haveKeysChanged = (state) => {
  if (
    !isEqual(Object.keys(initialStateFilters), Object.keys(state))
  ) {
    return true;
  }
  return Object.keys(initialStateFilters).reduce(
    (result, entityKey) => {
      return (
        !isEqual(
          Object.keys(initialStateFilters[entityKey]),
          Object.keys(state[entityKey]),
        ) || result
      );
    },
    false,
  );
};

export const filters = (state = initialStateFilters, action) => {
  if (
    action &&
    [
      'SUCCESS_LOGOUT',
      'SUCCESS_INTERNAL_LOGIN',
      'READ_AUTHENTICATED_USER',
    ].includes(action.type)
  ) {
    if (haveKeysChanged(state)) {
      return initialStateFilters;
    }
  }
  if (action.type === 'SET_UI_FILTER') {
    const toggleUiFilterAction = action as TypeToggleUiFilterAction;
    const oldFilterState =
      state[toggleUiFilterAction.entityName][
        toggleUiFilterAction.filter
      ].state;
    const newFilterState =
      oldFilterState === State.collapsed
        ? State.uncollapsed
        : State.collapsed;
    return {
      ...state,
      [toggleUiFilterAction.entityName]: {
        ...state[toggleUiFilterAction.entityName],
        [toggleUiFilterAction.filter]: {
          state: newFilterState,
        },
      },
    };
  }

  return state;
};

export const activeShareLink = (state = null, action) => {
  if (
    action.type === 'SUCCESS_READ_SHARE_LINK' &&
    !action.meta.denormalized_payload.deleted_at
  ) {
    return action.payload.result;
  }

  const errorCode =
    action.error && action.error.data && action.error.data.error_code;
  if (
    errorCode &&
    [
      ERRORS.SHARE_LINK_ORGANIZATION_EXPIRED,
      ERRORS.SHARE_LINK_ORGANIZATION_ALREADY_MEMBER,
      ERRORS.SHARE_LINK_NOT_FOUND,
      ERRORS.SHARE_LINK_DELETED,
      ERRORS.SHARE_LINK_ACCESS_DENIED,
    ].indexOf(errorCode) > -1
  ) {
    return null;
  }

  if (
    action.type.startsWith('REQUEST_READ') &&
    action.type.includes('GROUP')
  ) {
    return null;
  }

  if (action.type === 'SUCCESS_LOGOUT') {
    return null;
  }

  return state;
};

export const experimentWorkflowView = (
  state = 'table',
  action: TypeSetUiExperimentWorkflowViewAction,
) => {
  if (action.type === 'SET_UI_EXPERIMENT_WORKFLOW_VIEW') {
    return action.view;
  }

  return state;
};

export const searchParams = (
  state = {},
  action: TypeSetUiSearchParamsAction,
) => {
  if (action.type === 'SET_UI_SEARCH_PARAMS') {
    return {
      ...state,
      [action.pathname]: action.params,
    };
  }

  return state;
};

export const countPerPage = (
  state = 10,
  action: TypeSetUiCountPerPageAction,
) => {
  if (action.type === 'SET_UI_COUNT_PER_PAGE') {
    return action.count;
  }

  return state;
};

const reducers = combineReducers({
  filters,
  active_share_link: activeShareLink,
  experiment_workflow_view: experimentWorkflowView,
  count_per_page: countPerPage,
  search_params: searchParams,
});

export default reducers;
